import {
  calculateNinety,
  calculateBackToBack,
  calculateOffset,
  calculateSaddle3,
  calculateSaddle4,
  calculateKick,
  calculateRollingOffset,
  calculateParallelOffset,
  calculateConcentric,
  calculateSegmented,
  type ShoeConstants,
} from '@509-bender/bend-math';

export type InputType =
  | { kind: 'number'; min?: number; max?: number; step?: number; unit: string }
  | { kind: 'angle'; min?: number; max?: number; presets?: readonly number[] }; // preset angle picker with +/- degree nudges

export interface InputSpec {
  key: string;
  label: string;
  help?: string;
  default: number;
  type: InputType;
}

export interface CalculatorConfig {
  id: string; // url slug, e.g. "ninety"
  shortName: string; // "90°"
  displayName: string; // "90° Stub-up"
  description: string;
  /** Input fields shown in the form. */
  inputs: InputSpec[];
  /** Whether this bend uses a bender shoe (most do; concentric/segmented don't). */
  needsBender: boolean;
  /** Whether this calculator is part of the Pro upgrade. */
  requiresPro?: boolean;
  /**
   * Run the calculator with the form values and (optional) shoe.
   * Returns the raw result object from bend-math, or throws.
   */
  calculate: (values: Record<string, number>, shoe?: ShoeConstants) => unknown;
}

const angleInput: InputSpec = {
  key: 'angle',
  label: 'Bend angle',
  default: 30,
  type: { kind: 'angle' },
};

const wideAngleType: InputType = {
  kind: 'angle',
  min: 1,
  max: 180,
  presets: [30, 45, 60, 90, 180],
};

const segmentedPerBendAngleType: InputType = {
  kind: 'angle',
  min: 1,
  max: 45,
  presets: [2.5, 5, 10, 15, 22.5],
};

export const SADDLE_CENTER_ANGLE_PRESETS = [
  10, 15, 20, 22.5, 25, 27.5, 30, 32.5, 35, 37.5, 40, 42.5, 45, 47.5, 50, 52.5, 55, 57.5, 60,
] as const;

const saddleCenterAngleInput: InputSpec = {
  key: 'angle',
  label: 'Center bend',
  default: 45,
  type: { kind: 'angle', min: 10, max: 60, presets: SADDLE_CENTER_ANGLE_PRESETS },
};

export const CALCULATORS: CalculatorConfig[] = [
  {
    id: 'ninety',
    shortName: '90°',
    displayName: '90° Stub-up',
    description: 'Single 90° bend — measure the stub, get the mark.',
    needsBender: true,
    inputs: [
      {
        key: 'stubHeight',
        label: 'Stub height',
        default: 12,
        type: { kind: 'number', min: 1, step: 0.25, unit: 'in' },
      },
    ],
    calculate: (v, shoe) => calculateNinety({ stubHeight: v.stubHeight!, shoe: shoe! }),
  },
  {
    id: 'back-to-back',
    shortName: 'Back-to-back',
    displayName: 'Back-to-back 90°',
    description: 'Two 90°s a known distance apart.',
    needsBender: true,
    requiresPro: true,
    inputs: [
      {
        key: 'stub1',
        label: 'Stub 1 height',
        default: 12,
        type: { kind: 'number', min: 1, step: 0.25, unit: 'in' },
      },
      {
        key: 'betweenStubs',
        label: 'Distance between stubs',
        default: 24,
        type: { kind: 'number', min: 1, step: 0.25, unit: 'in' },
      },
      {
        key: 'stub2',
        label: 'Stub 2 height',
        default: 18,
        type: { kind: 'number', min: 1, step: 0.25, unit: 'in' },
      },
    ],
    calculate: (v, shoe) =>
      calculateBackToBack({
        stub1: v.stub1!,
        stub2: v.stub2!,
        betweenStubs: v.betweenStubs!,
        shoe: shoe!,
      }),
  },
  {
    id: 'offset',
    shortName: 'Offset',
    displayName: '2-point Offset',
    description: 'Rise over an obstruction; standard 2-bend offset.',
    needsBender: true,
    inputs: [
      {
        key: 'rise',
        label: 'Rise',
        default: 12.5,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'fromEnd',
        label: 'First mark from end',
        default: 8,
        type: { kind: 'number', min: 0, step: 0.25, unit: 'in' },
      },
      angleInput,
    ],
    calculate: (v, shoe) => calculateOffset({ rise: v.rise!, angle: v.angle!, shoe: shoe! }),
  },
  {
    id: 'saddle3',
    shortName: '3-point saddle',
    displayName: '3-point Saddle',
    description: 'Pick the center bend and get both side marks.',
    needsBender: true,
    inputs: [
      {
        key: 'obstructionHeight',
        label: 'Obstruction height',
        default: 2,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'centerMark',
        label: 'Center mark from end',
        default: 30,
        type: { kind: 'number', min: 1, step: 0.5, unit: 'in' },
      },
      saddleCenterAngleInput,
    ],
    calculate: (v, shoe) =>
      calculateSaddle3({
        obstructionHeight: v.obstructionHeight!,
        centerMark: v.centerMark!,
        centerAngle: v.angle!,
        shoe: shoe!,
      }),
  },
  {
    id: 'saddle4',
    shortName: '4-point saddle',
    displayName: '4-point Saddle',
    description: 'Two offsets across a wide obstruction.',
    needsBender: true,
    requiresPro: true,
    inputs: [
      {
        key: 'obstructionHeight',
        label: 'Obstruction height',
        default: 4,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'obstructionWidth',
        label: 'Obstruction width',
        default: 12,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      angleInput,
      {
        key: 'startMark',
        label: 'First mark from end',
        default: 12,
        type: { kind: 'number', min: 0, step: 0.5, unit: 'in' },
      },
    ],
    calculate: (v, shoe) =>
      calculateSaddle4({
        obstructionHeight: v.obstructionHeight!,
        obstructionWidth: v.obstructionWidth!,
        angle: v.angle!,
        startMark: v.startMark!,
        shoe: shoe!,
      }),
  },
  {
    id: 'kick',
    shortName: 'Kick',
    displayName: 'Kick (compound 90°)',
    description: '90° with a built-in offset to clear a knockout.',
    needsBender: true,
    requiresPro: true,
    inputs: [
      {
        key: 'stubHeight',
        label: 'Stub height',
        default: 18,
        type: { kind: 'number', min: 1, step: 0.25, unit: 'in' },
      },
      {
        key: 'kickOffset',
        label: 'Kick offset',
        default: 2,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      { ...angleInput, key: 'kickAngle', label: 'Kick angle' },
    ],
    calculate: (v, shoe) =>
      calculateKick({
        stubHeight: v.stubHeight!,
        kickOffset: v.kickOffset!,
        kickAngle: v.kickAngle!,
        shoe: shoe!,
      }),
  },
  {
    id: 'rolling-offset',
    shortName: 'Rolling offset',
    displayName: 'Rolling Offset (3D)',
    description: 'Offset that must rise AND roll to one side.',
    needsBender: true,
    requiresPro: true,
    inputs: [
      {
        key: 'rise',
        label: 'Rise',
        default: 3,
        type: { kind: 'number', min: 0, step: 0.25, unit: 'in' },
      },
      {
        key: 'roll',
        label: 'Roll',
        default: 4,
        type: { kind: 'number', min: 0, step: 0.25, unit: 'in' },
      },
      angleInput,
    ],
    calculate: (v, shoe) =>
      calculateRollingOffset({
        rise: v.rise!,
        roll: v.roll!,
        angle: v.angle!,
        shoe: shoe!,
      }),
  },
  {
    id: 'parallel-offset',
    shortName: 'Parallel offset',
    displayName: 'Parallel Offset',
    description: 'Stagger matching offsets to keep conduit spacing clean.',
    needsBender: true,
    requiresPro: true,
    inputs: [
      {
        key: 'rise',
        label: 'Rise',
        default: 10,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'fromEnd',
        label: 'Run 1 first mark',
        default: 8,
        type: { kind: 'number', min: 0, step: 0.25, unit: 'in' },
      },
      angleInput,
      {
        key: 'spacing',
        label: 'Center spacing',
        default: 4,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'conduitCount',
        label: 'Conduit count',
        default: 3,
        type: { kind: 'number', min: 2, max: 12, step: 1, unit: '' },
      },
    ],
    calculate: (v, shoe) =>
      calculateParallelOffset({
        rise: v.rise!,
        angle: v.angle!,
        spacing: v.spacing!,
        fromEnd: v.fromEnd!,
        conduitCount: v.conduitCount!,
        shoe: shoe!,
      }),
  },
  {
    id: 'concentric',
    shortName: 'Concentric',
    displayName: 'Concentric (parallel runs)',
    description: 'Matched parallel bends — each outer pipe needs more length.',
    needsBender: false,
    requiresPro: true,
    inputs: [
      {
        key: 'innerRadius',
        label: 'Inner radius',
        default: 12,
        type: { kind: 'number', min: 1, step: 0.5, unit: 'in' },
      },
      {
        key: 'spacing',
        label: 'Center-to-center spacing',
        default: 4,
        type: { kind: 'number', min: 0.25, step: 0.25, unit: 'in' },
      },
      {
        key: 'angle',
        label: 'Bend angle',
        default: 90,
        type: wideAngleType,
      },
      {
        key: 'parallelCount',
        label: 'Number of parallels',
        default: 3,
        type: { kind: 'number', min: 1, max: 20, step: 1, unit: '' },
      },
    ],
    calculate: (v) =>
      calculateConcentric({
        innerRadius: v.innerRadius!,
        spacing: v.spacing!,
        angle: v.angle!,
        parallelCount: v.parallelCount!,
      }),
  },
  {
    id: 'segmented',
    shortName: 'Segmented',
    displayName: 'Segmented Large-radius',
    description: 'Many small bends to approximate a long sweep on big pipe.',
    needsBender: false,
    requiresPro: true,
    inputs: [
      {
        key: 'totalRadius',
        label: 'Target radius',
        default: 36,
        type: { kind: 'number', min: 1, step: 1, unit: 'in' },
      },
      {
        key: 'totalAngle',
        label: 'Total bend angle',
        default: 90,
        type: wideAngleType,
      },
      {
        key: 'segmentAngle',
        label: 'Angle per segment',
        default: 5,
        type: segmentedPerBendAngleType,
      },
    ],
    calculate: (v) =>
      calculateSegmented({
        totalRadius: v.totalRadius!,
        totalAngle: v.totalAngle!,
        segmentAngle: v.segmentAngle!,
      }),
  },
];

export function getCalculatorById(id: string): CalculatorConfig | undefined {
  return CALCULATORS.find((c) => c.id === id);
}

export function isProCalculator(id: string): boolean {
  return getCalculatorById(id)?.requiresPro === true;
}
